import { useState, useEffect } from "react";
import { ConfigManager } from "@/application/config/Config";
import { secureStorage } from "@/utils/secureStorage";

interface ProSubscriptionProductProps {
  onClose?: () => void;
}

const PRODUCT_CONFIG = {
  swimmer: {
    title: "Upgrade to Swimming's Smartest Al",
    subtitle:
      "Empower your swimming journey with access to coaching tools, AI support, and a vibrant community.",
    features: [
      {
        title: "Product Marketplace Access",
        description: "Browse and purchase products listed by your coach.",
      },
      {
        title: "AI Agent Access",
        description:
          "Interact with your coach's AI agent and built-in AI tools.",
      },
      {
        title: "Community Chat",
        description: "Connect and chat with other swimmers and coaches.",
      },
      {
        title: "Coach Hiring",
        description:
          "Find and hire coaches directly through the platform.",
      },
      {
        title: "AI Practice Set Generator",
        description:
          "Create personalized practice sets using AI.",
      },
      {
        title: "Swimming Board Scanner",
        description:
          "Scan swimming boards to generate practice sets instantly.",
      },
    ],
    pricing: {
      monthly: { price: 9.99, label: "per month" },
      yearly: { price: 199.99, label: "per year" },
    },
  },

  coach: {
    title: "Upgrade to Coach smarter. Earn more.",
    subtitle:
      "Unlock powerful tools to manage your swimming business and support your swimmers with AI-driven features.",
    features: [
      {
        title: "Product Marketplace",
        description:
          "List and sell your swimming-related products.",
      },
      {
        title: "Custom AI Agent",
        description:
          "Build your own AI agent using your expertise to assist you and your swimmers.",
      },
      {
        title: "Community Chat",
        description:
          "Connect and chat with other coaches and swimmers.",
      },
      {
        title: "AI Practice Set Generator",
        description:
          "Use AI to create customized practice sets.",
      },
      {
        title: "Swimming Board Scanner",
        description:
          "Scan swimming boards to automatically generate practice sets.",
      },
    ],
    pricing: {
      monthly: { price: 19.99, label: "per month" },
      yearly: { price: 199.99, label: "per year" },
    },
  },
};



export function ProSubscriptionProduct({
  onClose,
}: ProSubscriptionProductProps) {
  const [loadingPlan, setLoadingPlan] = useState<"monthly" | "yearly" | null>(null);
  const config = ConfigManager.getInstance();
  const paymentConfig = config.getPaymentConfig();

  type UserType = "swimmer" | "coach";

  const userType =
    secureStorage.get<UserType>("user_type") ?? "swimmer";

  const productConfig = PRODUCT_CONFIG[userType];
  const paymentLinks = paymentConfig.links[userType];

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        setLoadingPlan(null);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const handleSubscribe = (plan: "monthly" | "yearly") => {
    setLoadingPlan(plan);

    const paymentLink = paymentLinks[plan];
    if (!paymentLink) {
      setLoadingPlan(null);
      return;
    }

    window.location.href = paymentLink;
  };

  return (
    <div className="relative max-w-4xl mx-auto p-6">
      {onClose && (
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 text-xl"
        >
          ✕
        </button>
      )}

      <h2 className="text-4xl font-bold text-center mb-4 dark:text-white">
        {productConfig.title}
      </h2>

      <p className="text-gray-600 dark:text-gray-300 text-center mb-8 text-lg">
        {productConfig.subtitle}
      </p>

      <ul className="space-y-4 mb-12">
        {productConfig.features.map((f, i) => (
          <li key={i} className="flex gap-3">
            <span className="text-blue-600 text-xl">✓</span>
            <div>
              <p className="font-semibold dark:text-white">{f.title}</p>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                {f.description}
              </p>
            </div>
          </li>
        ))}
      </ul>

      <p className="text-center text-sm text-gray-500 mb-6">
        Start with a <span className="font-semibold">7-day free trial</span>. No charge today.
        Cancel anytime.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* MONTHLY */}
        <div className="border rounded-2xl p-6 shadow bg-white dark:bg-gray-900">
          <h3 className="text-xl font-semibold dark:text-white mb-2">
            Monthly
          </h3>

          <p className="text-4xl font-bold text-blue-600 mb-1">
            ${productConfig.pricing.monthly.price}
          </p>

          <p className="text-gray-500 mb-6">
            {productConfig.pricing.monthly.label}
          </p>

          <button
            disabled={loadingPlan !== null}
            onClick={() => handleSubscribe("monthly")}
            className={`w-full py-3 rounded-lg text-white font-semibold ${
              loadingPlan === "monthly"
                ? "bg-gray-400"
                : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {loadingPlan === "monthly" ? "Redirecting..." : "Start Free Trial"}
          </button>
        </div>

        {/* YEARLY */}
        <div className="border rounded-2xl p-6 shadow bg-white dark:bg-gray-900 relative">
          <span className="absolute top-4 right-4 bg-green-600 text-white text-xs px-3 py-1 rounded-full">
            Best Value
          </span>

          <h3 className="text-xl font-semibold dark:text-white mb-2">
            Yearly
          </h3>

          <p className="text-4xl font-bold text-green-600 mb-1">
            ${productConfig.pricing.yearly.price}
          </p>

          <p className="text-gray-500 mb-6">
            {productConfig.pricing.yearly.label}
          </p>

          <button
            disabled={loadingPlan !== null}
            onClick={() => handleSubscribe("yearly")}
            className={`w-full py-3 rounded-lg text-white font-semibold ${
              loadingPlan === "yearly"
                ? "bg-gray-400"
                : "bg-green-600 hover:bg-green-700"
            }`}
          >
            {loadingPlan === "yearly" ? "Redirecting..." : "Start Free Trial"}
          </button>
        </div>
      </div>
    </div>
  );
}
